























import Vue from 'vue';
import Component from 'vue-class-component';
import { Emit, Prop, Watch } from 'vue-property-decorator';

import Node from '@/models/Node';
import Mutations from '@/store/mutations';
import { SendPacket } from '@/networking/SendPacket';
import ProjectServer from '@/models/ProjectServer';
import { PacketType } from '@/models/Packet';
import DateOptions from '@/models/DateOptions';

@Component({})
export default class NodeDatePicker extends Vue
{
    @Prop(Node) readonly node! : Node;
    
    // ------ optional booleans to adjust behavior; can all be set as true just by adding the name to the html tag ------
    // start targets dateStart, end targets dateEnd; either will do an inverted date check on edits
    //  - if neither are specified, dateStart is targeted and no inverted date check occurs
    // mutateAndSendPacket, if specified as true, will send a mutation to Vuex and a packet to the ProjectServer on edits
    //  - should be true for editor, false for creator
    @Prop(Boolean) readonly start! : boolean;
    @Prop(Boolean) readonly end! : boolean;
    @Prop(Boolean) readonly mutateAndSendPacket! : boolean;

    readonly minYear : number = -271820; // BCE
    readonly maxYear : number = 275760; // CE
	
    dateInput =
    {
        month: <any>undefined,
        day: <any>undefined,
        year: <any>undefined,
        isBCE: false
    };
    validatorDate : any = null;
    error : any = null;

    hasDuration = true;

    mounted()
    {
        if (this.end && this.node.dateEnd != null && !isNaN(this.node.dateEnd.valueOf()))
        {
            this.dateInput.year = this.node.dateEnd.getUTCFullYear();
            this.dateInput.month = this.node.dateEnd.getUTCMonth();
            this.dateInput.day = this.node.dateEnd.getUTCDate();
        }
        else if (this.node.dateStart != null && !isNaN(this.node.dateStart.valueOf()))
        {
            this.dateInput.year = this.node.dateStart.getUTCFullYear();
            this.dateInput.month = this.node.dateStart.getUTCMonth();
            this.dateInput.day = this.node.dateStart.getUTCDate();
        }

        if (this.node.dateStart == null || this.node.dateEnd == null)
        {
            this.hasDuration = true;
        }
        else
        {
            this.hasDuration = (this.node.dateStart.getTime() !== this.node.dateEnd.getTime());
        }
    }

    get formatHint()
    {
        if (!(this.useMonth))
        {
            return '(Year)';
        }
        else if (!(this.useDay))
        {
            return '(Month/Year)';
        }
        else
        {
            return '(Month/Day/Year)';
        }
    }

    get useDay()
    {
        let hideDay = (this.isBC()) || (this.node.dateOptions.has(DateOptions.HideDay));
        if (hideDay)
        {
            this.editDay = 1;
        }
        return !hideDay;
    }
    
    get useMonth()
    {
        let hideMonth = (this.isBC()) || (this.node.dateOptions.has(DateOptions.HideMonth));
        if (hideMonth)
        {
            this.editMonth = 1;
        }
        return !hideMonth;
    }

    get editHasDuration()
    {
        return this.hasDuration;
    }
    set editHasDuration(value) 
    {
        this.hasDuration = value;
        this.$emit('has-duration-changed', this.hasDuration);
        if (!(this.hasDuration))
        {
            this.validatorDate = new Date(this.node.dateStart.getTime());
            this.dateInput.year = this.validatorDate.getUTCFullYear();
            this.dateInput.month = this.validatorDate.getUTCMonth();
            this.dateInput.day = this.validatorDate.getUTCDate();
            this.dateInput.isBCE = this.dateInput.year < 0;
            this.updateEndIfChanged();
        }
    }

	get editMonth()
    {
        return this.dateInput.month != null ? this.dateInput.month+1 : '';
    }
    set editMonth(value)
    {
        if (value !== this.editMonth)
        {
            if (value != 0)
            {
                let parse = Number.parseInt(value);
                this.dateInput.month = isNaN(parse) ? undefined : (parse - 1);
                this.updateDate();
            }
            else
            {
                this.dateInput.month = undefined;
            }
            
            this.$nextTick(() => {
                if (this.$refs.month != null)
                {
                    (this.$refs.month as any).focus();
                }
            });
        }
    }

    get editDay()
    {
        return this.dateInput.day != null ? this.dateInput.day : '';
    }
    set editDay(value)
    {
        if (value !== this.editDay)
        {
            if (value != 0)
            {
                let parse = Number.parseInt(value);
                this.dateInput.day = isNaN(parse) ? undefined : parse;
                this.updateDate();
            }
            else
            {
                this.dateInput.day = undefined;
            }
           
            this.$nextTick(() => {
                    if (this.$refs.day != null)
                {
                    (this.$refs.day as any).focus();
                }
            });
        }
    }

	
    get editYear()
    {
        return this.dateInput.year != null ? Math.abs(this.dateInput.year) : '';
    }
    set editYear(value : any)
    {
        if (value !== this.editYear)
        {
            if (value !== '')
            {
                let parse = Number.parseInt(value);
                this.dateInput.year = isNaN(parse) ? undefined : parse * ((this.dateInput.isBCE) ? -1 : 1);
                this.updateDate();
            }
            else
            {
                this.dateInput.year = undefined;
            }
            
            this.$nextTick(() => {
                if (this.$refs.year != null)
                {   
                    (this.$refs.year as any).focus();
                }
            });
        }
    }

    get editBCE()
    {
        if (this.dateInput.year != null)
        {
            this.dateInput.isBCE = (this.dateInput.year < 0);
        }
        return this.dateInput.isBCE;
    }
    set editBCE(value)
    {
        this.dateInput.isBCE = value;
        this.dateInput.year = isNaN(this.dateInput.year) ? undefined : (Math.abs(this.dateInput.year) * (value ? -1 : 1));
        this.updateDate();
       
        this.$nextTick(() => {
                if (this.$refs.bce != null)
            {
                (this.$refs.bce as any).focus();
            }
        });
    }

    formValid() : boolean
    {
        return this.validatorDate != null && !isNaN(this.validatorDate.valueOf());
    }

    public updateDate()
    {
        if (this.end && !(this.hasDuration))
        {
            this.validatorDate = new Date(this.node.dateStart.getTime());
            this.dateInput.year = this.validatorDate.getUTCFullYear();
            this.dateInput.month = this.validatorDate.getUTCMonth();
            this.dateInput.day = this.validatorDate.getUTCDate();
            this.dateInput.isBCE = this.dateInput.year < 0;
        }

        if (!isNaN(this.dateInput.year))
        {
            if (this.dateInput.year < this.minYear || this.dateInput.year > this.maxYear)
            {
                this.error = `The date must be within the range 271820 BCE - 09/13/275760 CE. This limitation will be relaxed in the future.`;
                this.error += 
                    (this.end && this.node.dateEnd != null) ? ` You could use ${this.node.dateEnd.getUTCBCString()} for the end date instead.` 
                        : ((this.start && this.node.dateStart != null) ? ` You could use ${this.node.dateStart.getUTCBCString()} for the start date instead.` : '');
                return;
            }

            if (this.dateInput.year < 0 || (!isNaN(this.dateInput.month) && !isNaN(this.dateInput.day)))
            {
                if (this.validatorDate == null)
                {
                    this.validatorDate = new Date(0);
                }
                this.validatorDate.setUTCFullYear(this.dateInput.year);
                this.validatorDate.setUTCMonth((this.dateInput.year < 0) ? 0 : this.dateInput.month);
                this.validatorDate.setUTCDate((this.dateInput.year < 0) ? 1 : this.dateInput.day);
                if (!isNaN(this.validatorDate.valueOf()))
                {
                    if (this.end)
                    {
                        if (this.node.dateStart != null 
                            && this.node.dateStart > this.validatorDate)
                        {
                            // notify of inversion
                            this.error = `You specified an end date that occurs before the start date.${this.node.dateEnd != null ? ` You could use ${this.node.dateEnd.getUTCBCString()} for the end date instead.` : ''}`;
                        }
                        else if (this.node.isEra() && this.node.dateStart.getTime() === this.validatorDate.getTime())
                        {
                            this.error = `You specified an end date that does not occur after the start date, which is not allowed for Eras.${this.node.dateEnd != null ? ` You could use ${this.node.dateEnd.getUTCBCString()} for the end date instead.` : ''}`;
                        }
                        else
                        {
                            this.updateEndIfChanged();
                        }
                    }
                    else if (this.start)
                    {
                        if (this.node.dateEnd != null 
                            && this.node.dateEnd < this.validatorDate)
                        {
                            // notify of inversion
                            this.error = `You specified a start date that occurs after the end date.${this.node.dateStart != null ? ` You could use ${this.node.dateStart.getUTCBCString()} for the start date instead.` : ''}`;
                        }
                        else if (this.node.isEra() && this.node.dateEnd.getTime() === this.validatorDate.getTime())
                        {
                            this.error = `You specified an start date that does not occur before the end date, which is not allowed for Eras.${this.node.dateStart != null ? ` You could use ${this.node.dateStart.getUTCBCString()} for the start date instead.` : ''}`;
                        }
                        else
                        {
                            this.updateStartIfChanged();
                        }
                    }
                    else // no inversion check, assume that you want to write to dateStart
                    {
                        this.updateStartIfChanged();
                    }
                }
                else
                {
                    this.error = `Not a valid date.`;
                    this.error += 
                        (this.end && this.node.dateEnd != null) ? ` You could use ${this.node.dateEnd.getUTCBCString()} for the end date instead.` 
                            : ((this.start && this.node.dateStart != null) ? ` You could use ${this.node.dateStart.getUTCBCString()} for the start date instead.` : '');
                }
            }
        }
    }

    updateStartIfChanged()
    {
        this.error = null;

        let changed = (this.node.dateStart == null || this.node.dateStart.valueOf() !== this.validatorDate.valueOf());
        if (this.node.dateStart == null)
        {
            this.node.dateStart = new Date(this.validatorDate.valueOf());
        }
        else if (changed)
        {
            this.node.dateStart.setTime(this.validatorDate.valueOf());
        }

        if (changed)
        {
            if (this.mutateAndSendPacket)
            {
                this.$store.commit(Mutations.updateNodeStart, { nodeid: this.node.nodeid, value: this.node.dateStart });
                SendPacket(PacketType.NodeUpdateStart, { nodeid: this.node.nodeid, value: this.node.dateStart.valueOf() });
            }

            this.$emit('date-changed');
        }
    }

    updateEndIfChanged()
    {
        this.error = null;

        let changed = (this.node.dateEnd == null || this.node.dateEnd.valueOf() !== this.validatorDate.valueOf());
        if (this.node.dateEnd == null)
        {
            this.node.dateEnd = new Date(this.validatorDate.valueOf());
        }
        else if (changed)
        {
            this.node.dateEnd.setTime(this.validatorDate.valueOf());
        }

        if (changed)
        {
            if (this.mutateAndSendPacket)
            {
                this.$store.commit(Mutations.updateNodeEnd, { nodeid: this.node.nodeid, value: this.node.dateEnd });
                SendPacket(PacketType.NodeUpdateEnd, { nodeid: this.node.nodeid, value: this.node.dateEnd.valueOf() });
            }

            this.$emit('date-changed');
        }
    }

    isBC()
    {
        return !isNaN(this.dateInput.year) && this.dateInput.year < 0;
    }
}
